import { taskFilterEnum } from '@frontend/api-client';
import { RECURRENCE_STREAM_TYPE_ENUM } from '@frontend/api-types';
import { values } from '@frontend/duck-tape';
import { z } from 'zod';

const coercedNumber = () =>
  z.preprocess((value) => {
    if (typeof value === 'string') {
      const trimmedValue = value.trim();
      if (trimmedValue === '') return undefined;
      const numericValue = Number(trimmedValue);
      return Number.isFinite(numericValue) ? numericValue : value;
    }
    return value;
  }, z.number());

const readonlyStringEnum = <T extends string>(enumValues: T[]) => z.enum(enumValues as [T, ...T[]]);

const v = {
  boolean: z.boolean,
  coercedNumber,
  object: z.object,
  readonlyStringEnum,
  string: z.string,
  uuid: () => z.string().uuid(),
};

export const PROFILE_ACTION = {
  upgrade: 'upgrade',
} as const;

export type ProfileAction = (typeof PROFILE_ACTION)[keyof typeof PROFILE_ACTION];

/**
 * Some notes for the route validators:
 * - On mobile, these validators are used for both query and param parsing/validation. For example, the route `/app/draft-task/[draftTaskId]`'s draftTaskId will be fully validated.
 *   However on web, `/app/draft-task/$draftTaskId` will not use the zod validator for the draftTaskId, since the path params are interpreted by the tanstack router (and we can't
 *   strictly validate them with Zod). Technically to reuse the same validator for both platforms, we should omit the "path" params when we use one on web.
 */

export const acceptInviteRouteValidator = v.object({
  inviteId: v.uuid(),
  promo: v.string().optional(),
});

export const signUpRouteValidator = v.object({
  description: v.string().optional(),
  im_ref: v.string().optional(),
  inviteId: v.uuid().optional(),
  irclickid: v.string().optional(),
  promo: v.string().optional(),
  rdt_cid: v.string().optional(),
  referrer: v.uuid().optional(),
  twclid: v.string().optional(),
  utm_campaign: v.string().optional(),
  utm_content: v.string().optional(),
  utm_medium: v.string().optional(),
  utm_source: v.string().optional(),
  utm_term: v.string().optional(),
});

export type SignUpSearchParams = z.output<typeof signUpRouteValidator>;

export const waitListRouteValidator = signUpRouteValidator;

export const onboardingRouteValidator = v.object({
  description: v.string().optional(),
});

export type OnboardingSearchParams = z.output<typeof onboardingRouteValidator>;

export const tasksRouteValidator = v
  .object({
    activeTab: v.readonlyStringEnum(values(taskFilterEnum)).optional(),
  })
  .optional();

export const draftTaskRouteValidator = v.object({ draftTaskId: v.string() });

export const createDraftTaskRouteValidator = v.object({
  description: v.string().optional(),
  example: v.string().optional(),
  exampleType: v.string().optional(),
  isFromSignUp: v.boolean().optional(),
  letterRecommendationId: v.string().optional(),
  letterRecommendationTaskSuggestionIdx: v.coercedNumber().optional(),
  name: v.string().optional(),
  suggestionId: v.string().optional(),
});

export const optionSheetRouteValidator = v.object({ optionSheetId: v.string() });

export const optionSheetOptionsRouteValidator = v.object({ optionId: v.string(), optionSheetId: v.string() });

export const recurrenceDetailRouteValidator = v.object({
  recurrenceId: v.string(),
});

export const createRecurrenceRouteValidator = v
  .object({
    type: v.readonlyStringEnum(values(RECURRENCE_STREAM_TYPE_ENUM)).optional(),
  })
  .optional();

export const secureNoteRouteValidator = v.object({
  secureNoteId: v.string(),
  secureNoteTitle: v.string(),
  taskId: v.string().optional(),
});

export const createSecureNoteRouteValidator = v
  .object({
    initialPassword: v.string().optional(),
    taskId: v.string().optional(),
  })
  .optional();

export const feedbackRouteValidator = z
  .object({
    draftId: v.string().optional(),
    taskId: v.string().optional(),
  })
  .optional();

export const taskRouteValidator = v.object({
  autoFocusTextInput: v.boolean().optional(),
  clearChatDraftAfterSecureNoteId: v.string().optional(),
  taskId: v.string(),
});

export const recurrenceSuggestionRouteValidator = v.object({
  type: v.readonlyStringEnum(values(RECURRENCE_STREAM_TYPE_ENUM)),
});

export const recurrenceSuggestionCreateRouteValidator = v.object({
  type: v.readonlyStringEnum(values(RECURRENCE_STREAM_TYPE_ENUM)),
});

export const creditHistoryRouteValidator = v.object({});

export const invalidPermissionsRouteValidator = v
  .object({
    blockedExplanation: v.string().optional(),
  })
  .optional();

export const profileRouteValidator = v.object({
  action: v.readonlyStringEnum(values(PROFILE_ACTION)).optional().catch(undefined),
  promo: v.string().optional(),
});

export type ProfileSearchParams = z.output<typeof profileRouteValidator>;
